import { DomOverlay } from './DomOverlay';
import { Haptics } from '../Haptics';
import { CodexManager } from '../CodexManager';
import { ShopManager } from '../ShopManager';
import { ProgressManager } from '../ProgressManager';
import { SaveManager } from '../SaveManager';
import { Starfield } from './Starfield';
import { FAMILY_COLORS, Family } from '../../config';

/**
 * Events emitted back to MenuScene.
 */
type HubEvents = {
  play: { resume: boolean };
  codex: void;
  shop: void;
  settings: void;
  toast: { message: string };
};

/**
 * HubOverlay — dashboard hub replacing the minimal Play/Codex/Shop/Settings
 * menu. Wires real data from ProgressManager / ShopManager / CodexManager +
 * SaveManager (for active-run detection).
 *
 * Hidden vs. design package because the underlying systems don't exist yet:
 *   - Offline-rewards banner (no offline accrual)
 *   - Energy / Overcharge cards (in-run only, not meaningful between runs)
 *   - Battle Pass / Stats nav entries (toast "Coming soon")
 */
export class HubOverlay extends DomOverlay<HubEvents> {
  private codex: CodexManager;
  private shop: ShopManager;
  private progress: ProgressManager;
  private hasActiveRun = false;

  constructor() {
    super();
    this.codex = new CodexManager();
    this.shop = new ShopManager();
    this.progress = new ProgressManager();

    const saved = SaveManager.readLocal();
    if (saved.ok) {
      try {
        const game = SaveManager.loadSaveData(
          saved.data,
          this.codex,
          this.shop,
          this.progress
        );
        this.hasActiveRun = Array.isArray(game.gridState) && game.gridState.length > 0;
      } catch {
        this.hasActiveRun = false;
      }
    }
  }

  protected render(): void {
    this.rootEl.innerHTML = '';
    this.rootEl.classList.add('hub-root');
    new Starfield(this.rootEl).mount();
    this.rootEl.appendChild(this.buildContent());
    this.injectStyles();
  }

  private buildContent(): HTMLDivElement {
    const wrap = this.el('div', 'hub-wrap');

    wrap.appendChild(this.buildTopBar());
    wrap.appendChild(this.buildXpBar());
    wrap.appendChild(this.buildHero());
    wrap.appendChild(this.buildPrimaryCta());
    const streak = this.buildDailyStreakCard();
    if (streak) wrap.appendChild(streak);
    wrap.appendChild(this.buildResourcesRow());
    wrap.appendChild(this.buildCodexTeaser());
    wrap.appendChild(this.buildSecondaryNav());
    wrap.appendChild(this.el('div', 'hub-version type-micro', 'v0.4.0 — FRACTURED CYBER-MINIMALISM'));

    return wrap;
  }

  // ────────────────────────────────────────────────────────────────────
  private buildTopBar(): HTMLDivElement {
    const level = this.progress.getLevel();
    const bar = this.el('div', 'hub-topbar');

    const left = this.el('div', 'hub-topbar__left');
    const avatar = this.el('div', 'hub-avatar', 'P');
    const info = this.el('div', 'hub-avatar__info');
    info.appendChild(this.el('div', 'hub-avatar__name', 'Player'));
    const stat = this.el('div', 'hub-avatar__stat');
    stat.innerHTML = `LV.${level.level} · ${level.xp}<span>/${level.xpToNext}</span>`;
    info.appendChild(stat);
    left.appendChild(avatar);
    left.appendChild(info);

    const right = this.el('div', 'hub-topbar__right');
    right.appendChild(this.iconButton('Achievements', GEAR_TROPHY_SVG, () => {
      this.emit('toast', { message: 'Achievements — coming soon' });
    }));
    right.appendChild(this.iconButton('Settings', GEAR_SVG, () => {
      Haptics.selection();
      this.emit('settings', undefined as never);
    }));

    bar.appendChild(left);
    bar.appendChild(right);
    return bar;
  }

  private buildXpBar(): HTMLDivElement {
    const level = this.progress.getLevel();
    const pct = Math.min(100, Math.round((level.xp / Math.max(1, level.xpToNext)) * 100));
    const bar = this.el('div', 'hub-xp');
    const fill = this.el('div', 'hub-xp__fill');
    fill.style.width = `${pct}%`;
    bar.appendChild(fill);
    return bar;
  }

  private buildHero(): HTMLDivElement {
    const hero = this.el('div', 'hub-hero');
    const glyph = this.el('div', 'hub-hero__glyph');
    glyph.innerHTML = PRISM_LOGO_SVG;
    hero.appendChild(glyph);
    hero.appendChild(this.el('h1', 'hub-hero__wordmark type-display', 'SPLINTR'));
    hero.appendChild(this.el('p', 'hub-hero__tagline type-micro', 'Merge · Splinter · Discover'));
    return hero;
  }

  private buildPrimaryCta(): HTMLButtonElement {
    const label = this.hasActiveRun ? 'Continue Run' : 'Start Run';
    const btn = this.el('button', 'btn btn--cta hub-cta', '');
    btn.innerHTML = `<svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><path d="M3 1.5v11l9-5.5z"/></svg><span>${label}</span>`;
    btn.addEventListener('click', () => {
      Haptics.medium();
      this.emit('play', { resume: this.hasActiveRun });
    });
    return btn;
  }

  private buildDailyStreakCard(): HTMLDivElement | null {
    const streak = this.progress.getLoginStreak();
    const rewards = ProgressManager.getDailyRewards();
    if (!rewards || rewards.length === 0) return null;

    const dayIndex = Math.min(
      Math.max(1, streak.currentStreak || 1),
      rewards.length
    );
    const today = rewards[dayIndex - 1];
    const claimed = !!streak.claimedToday;

    const card = this.el('div', 'hub-streak glass-panel');

    const head = this.el('div', 'hub-streak__head');
    const headLeft = this.el('div');
    headLeft.appendChild(this.el(
      'div',
      'hub-streak__kicker mono-label',
      `Daily Streak · Day ${dayIndex}`
    ));
    headLeft.appendChild(this.el(
      'div',
      'hub-streak__title',
      claimed ? 'Claimed today' : `+${today.shards} Shards · +${today.coins} Coins`
    ));
    head.appendChild(headLeft);

    const claimBtn = this.el(
      'button',
      `hub-streak__claim ${claimed ? 'is-claimed' : ''}`,
      claimed ? 'Claimed' : 'Claim'
    );
    if (claimed) {
      claimBtn.disabled = true;
    } else {
      claimBtn.addEventListener('click', () => {
        const today = new Date().toISOString().slice(0, 10);
        const reward = this.progress.checkIn(today);
        if (reward) {
          this.shop.addShards(reward.shards);
          this.shop.addCoins(reward.coins);
          Haptics.success();
          this.render();
        }
      });
    }
    head.appendChild(claimBtn);
    card.appendChild(head);

    const track = this.el('div', 'hub-streak__track');
    rewards.forEach((r, i) => {
      const day = i + 1;
      const state =
        day < dayIndex ? 'past' : day === dayIndex ? 'today' : 'future';
      const cell = this.el('div', `hub-streak__day is-${state}${day === 7 ? ' is-weekly' : ''}`);
      cell.appendChild(this.el('div', 'hub-streak__day-label mono-label', `D${day}`));
      const dot = this.el('div', 'hub-streak__day-dot');
      dot.innerHTML = STREAK_STAR_SVG;
      cell.appendChild(dot);
      cell.appendChild(this.el('div', 'hub-streak__day-val mono-stat', String(r.shards)));
      track.appendChild(cell);
    });
    card.appendChild(track);

    return card;
  }

  private buildResourcesRow(): HTMLDivElement {
    const wallet = this.shop.getWallet();
    const row = this.el('div', 'hub-resources');

    row.appendChild(this.buildCurrencyCard(
      'Shards',
      wallet.shards,
      'var(--accent-purple)',
      SHARDS_ICON_SVG
    ));
    row.appendChild(this.buildCurrencyCard(
      'Coins',
      wallet.coins,
      'var(--accent-gold)',
      COINS_ICON_SVG
    ));

    return row;
  }

  private buildCurrencyCard(
    label: string,
    value: number,
    color: string,
    iconSvg: string
  ): HTMLDivElement {
    const card = this.el('div', 'hub-currency');
    card.style.setProperty('--currency-color', color);
    const icon = this.el('div', 'hub-currency__icon');
    icon.innerHTML = iconSvg;
    card.appendChild(icon);
    const info = this.el('div', 'hub-currency__info');
    info.appendChild(this.el('div', 'hub-currency__label mono-label', label));
    info.appendChild(this.el('div', 'hub-currency__value mono-stat', value.toLocaleString()));
    card.appendChild(info);
    return card;
  }

  private buildCodexTeaser(): HTMLButtonElement {
    const stats = this.codex.getTotalStats();
    const hint = this.codex.computeNextDiscoveryHint();

    const btn = this.el('button', 'hub-codex glass-panel');
    btn.type = 'button';

    const ringEl = this.el('div', 'hub-codex__ring');
    ringEl.innerHTML = this.progressRingSvg(stats.percent, 56);
    btn.appendChild(ringEl);

    const body = this.el('div', 'hub-codex__body');
    const header = this.el('div', 'hub-codex__header');
    header.appendChild(this.el('span', 'hub-codex__kicker mono-label', 'Codex'));
    header.appendChild(this.el('span', 'hub-codex__count mono-label', `· ${stats.discovered}/${stats.total}`));
    body.appendChild(header);

    if (hint) {
      const famColor = this.familyColorCss(hint.family);
      const line1 = this.el('div', 'hub-codex__line1');
      line1.innerHTML = `Next: Merge 2 <span class="hub-codex__fam" style="color:${famColor}">${hint.familyLabel} ${hint.prevTierName}</span>`;
      body.appendChild(line1);
      const line2 = this.el('div', 'hub-codex__line2');
      line2.innerHTML = `→ discover the <b>${hint.currTierName}</b> tier`;
      body.appendChild(line2);
    } else {
      body.appendChild(this.el('div', 'hub-codex__line1', 'All elements discovered'));
    }
    btn.appendChild(body);

    const chevron = this.el('div', 'hub-codex__chevron');
    chevron.innerHTML = `<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M5 2l5 5-5 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
    btn.appendChild(chevron);

    btn.addEventListener('click', () => {
      Haptics.selection();
      this.emit('codex', undefined as never);
    });
    return btn;
  }

  private buildSecondaryNav(): HTMLDivElement {
    const nav = this.el('div', 'hub-nav');
    nav.appendChild(this.navButton('Shop', SHOP_SVG, () => {
      Haptics.selection();
      this.emit('shop', undefined as never);
    }));
    nav.appendChild(this.navButton('Battle Pass', PASS_SVG, () => {
      this.emit('toast', { message: 'Battle Pass — coming soon' });
    }));
    nav.appendChild(this.navButton('Stats', STATS_SVG, () => {
      this.emit('toast', { message: 'Stats — coming soon' });
    }));
    return nav;
  }

  // ────────────────────────────────────────────────────────────────────
  private iconButton(label: string, svg: string, onClick: () => void): HTMLButtonElement {
    const btn = this.el('button', 'hub-iconbtn');
    btn.setAttribute('aria-label', label);
    btn.innerHTML = svg;
    btn.addEventListener('click', onClick);
    return btn;
  }

  private navButton(label: string, svg: string, onClick: () => void): HTMLButtonElement {
    const btn = this.el('button', 'hub-navbtn');
    btn.innerHTML = `${svg}<span class="mono-label">${label}</span>`;
    btn.addEventListener('click', onClick);
    return btn;
  }

  private familyColorCss(family: Family): string {
    const hex = FAMILY_COLORS[family];
    return `#${hex.toString(16).padStart(6, '0')}`;
  }

  private progressRingSvg(percent: number, size: number): string {
    const r = (size / 2) - 4;
    const c = 2 * Math.PI * r;
    const dash = (percent / 100) * c;
    return `
<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" class="progress-ring">
  <circle cx="${size / 2}" cy="${size / 2}" r="${r}" class="progress-ring__track"/>
  <circle cx="${size / 2}" cy="${size / 2}" r="${r}" class="progress-ring__meter"
          stroke-dasharray="${dash} ${c}"
          transform="rotate(-90 ${size / 2} ${size / 2})"/>
  <text x="50%" y="52%" text-anchor="middle" dominant-baseline="middle" class="progress-ring__label">${percent}%</text>
</svg>`;
  }

  private injectStyles(): void {
    if (document.getElementById('hub-overlay-styles')) return;
    const style = document.createElement('style');
    style.id = 'hub-overlay-styles';
    style.textContent = HUB_STYLES;
    document.head.appendChild(style);
  }
}

// ═════════════════════════════════════════════════════════════════════
// Inline SVG icons — kept as constants so the module stays tree-shakeable
// without pulling in a sprite sheet. All use currentColor for themed tinting.
// ═════════════════════════════════════════════════════════════════════
const PRISM_LOGO_SVG = `
<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
  <defs>
    <linearGradient id="hub-prism" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#00F0FF"/>
      <stop offset="100%" stop-color="#0066FF"/>
    </linearGradient>
  </defs>
  <polygon points="50,8 92,50 50,92 8,50" fill="none" stroke="url(#hub-prism)" stroke-width="3"/>
  <polygon points="50,22 78,50 50,78 22,50" fill="url(#hub-prism)" opacity="0.45"/>
  <polyline points="22,50 50,22 78,50" fill="none" stroke="#F5F7FA" stroke-width="2" opacity="0.9"/>
</svg>`;

const GEAR_SVG = `
<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
  <circle cx="8" cy="8" r="2" stroke="currentColor" stroke-width="1.4"/>
  <path d="M8 1v2M8 13v2M1 8h2M13 8h2M3.05 3.05l1.41 1.41M11.54 11.54l1.41 1.41M3.05 12.95l1.41-1.41M11.54 4.46l1.41-1.41" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
</svg>`;

const GEAR_TROPHY_SVG = `
<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path d="M4 2h8v4a4 4 0 0 1-8 0V2z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>
  <path d="M6 10h4v3H6z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>
  <path d="M5 13h6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
</svg>`;

const STREAK_STAR_SVG = `
<svg width="12" height="12" viewBox="0 0 10 10" fill="none">
  <path d="M5 0.5l1.5 3 3 0.5-2.2 2 0.5 3L5 8l-2.8 1 0.5-3-2.2-2 3-0.5z" fill="currentColor"/>
</svg>`;

const SHARDS_ICON_SVG = `
<svg width="16" height="16" viewBox="0 0 14 14" fill="none">
  <path d="M7 1l3 3-3 9-3-9z" fill="currentColor" stroke="currentColor" stroke-width="1" stroke-linejoin="round"/>
</svg>`;

const COINS_ICON_SVG = `
<svg width="16" height="16" viewBox="0 0 14 14" fill="none">
  <circle cx="7" cy="7" r="5.5" fill="currentColor" stroke="currentColor" stroke-width="1"/>
  <circle cx="7" cy="7" r="3.2" fill="none" stroke="#050507" stroke-width="0.8" opacity="0.4"/>
</svg>`;

const SHOP_SVG = `
<svg width="18" height="18" viewBox="0 0 18 18" fill="none">
  <path d="M3 6h12l-1 9H4L3 6z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>
  <path d="M6 6V4a3 3 0 1 1 6 0v2" stroke="currentColor" stroke-width="1.4"/>
</svg>`;

const PASS_SVG = `
<svg width="18" height="18" viewBox="0 0 18 18" fill="none">
  <path d="M2 5l7-3 7 3v5a8 8 0 0 1-7 7 8 8 0 0 1-7-7V5z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>
  <path d="M6 8l2 2 4-4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

const STATS_SVG = `
<svg width="18" height="18" viewBox="0 0 18 18" fill="none">
  <path d="M3 15V9M9 15V3M15 15v-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
</svg>`;

// ═════════════════════════════════════════════════════════════════════
const HUB_STYLES = `
.hub-root { position: relative; width: 100%; height: 100%; overflow-y: auto; overflow-x: hidden; }
.hub-root .starfield { z-index: 0; }
.hub-root > .hub-wrap { position: relative; z-index: 2; }
.hub-wrap {
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  padding: 18px 16px 28px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  color: var(--text-primary);
  animation: spring-in 560ms var(--ease-spring) both;
}
.hub-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
.hub-topbar__left { display: flex; align-items: center; gap: 10px; }
.hub-avatar {
  width: 34px; height: 34px; border-radius: 50%;
  background: linear-gradient(135deg, var(--accent-primary), var(--accent-primary-deep));
  display: flex; align-items: center; justify-content: center;
  font-family: 'Space Grotesk', sans-serif; font-weight: 700; font-size: 14px;
  color: #050507;
  box-shadow: 0 0 16px -4px var(--accent-primary);
}
.hub-avatar__info { line-height: 1.1; }
.hub-avatar__name { font-size: 13px; font-weight: 600; color: var(--text-primary); }
.hub-avatar__stat {
  font-family: 'JetBrains Mono', monospace; font-size: 10px;
  color: var(--text-secondary); letter-spacing: 0.08em; margin-top: 2px;
}
.hub-avatar__stat span { color: var(--border-default); }
.hub-topbar__right { display: flex; gap: 8px; }
.hub-iconbtn {
  width: 36px; height: 36px; border-radius: var(--r-lg, 10px);
  background: rgba(21, 24, 33, 0.6);
  border: 1px solid rgba(73, 86, 112, 0.3);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; padding: 0; color: var(--text-secondary);
  transition: border-color 0.2s, color 0.2s;
}
.hub-iconbtn:hover, .hub-iconbtn:focus-visible {
  border-color: rgba(var(--accent-primary-rgb), 0.5);
  color: var(--accent-primary);
  outline: none;
}
.hub-xp {
  height: 3px; background: rgba(73, 86, 112, 0.2); border-radius: 2px;
  overflow: hidden; margin-bottom: 6px;
}
.hub-xp__fill {
  height: 100%;
  background: linear-gradient(90deg, var(--accent-primary), var(--accent-primary-deep));
  box-shadow: 0 0 10px var(--accent-primary);
  transition: width 0.4s var(--ease-spring);
}
.hub-hero {
  display: flex; flex-direction: column; align-items: center;
  padding: 8px 0 10px;
}
.hub-hero__glyph {
  width: 88px; height: 88px;
  filter: drop-shadow(0 0 22px rgba(var(--accent-primary-rgb), 0.55));
  animation: spring-in 700ms var(--ease-spring) both;
}
.hub-hero__glyph svg { width: 100%; height: 100%; }
.hub-hero__wordmark {
  font-size: 40px; font-weight: 700; letter-spacing: 0.14em;
  color: var(--text-primary); margin: 8px 0 0;
  text-shadow: 0 0 32px rgba(var(--accent-primary-rgb), 0.45);
}
.hub-hero__tagline {
  font-family: 'JetBrains Mono', monospace;
  font-size: 9px; letter-spacing: 0.28em;
  color: var(--text-secondary); text-transform: uppercase;
  margin-top: 4px;
}
.hub-cta {
  width: 100%;
  padding: 16px 20px;
  border-radius: var(--r-2xl, 14px);
  border: none;
  background: linear-gradient(135deg, var(--accent-primary), var(--accent-primary-deep));
  color: #050507;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px; font-weight: 700; letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  box-shadow:
    0 0 32px -4px var(--accent-primary),
    0 0 0 1px rgba(var(--accent-primary-rgb), 0.4) inset;
  display: flex; align-items: center; justify-content: center; gap: 10px;
  transition: transform 0.15s var(--ease-spring);
  min-height: 48px;
}
.hub-cta:active { transform: scale(0.98); }

.hub-streak { padding: 14px; }
.hub-streak__head {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 10px;
}
.hub-streak__kicker {
  color: var(--accent-primary);
  letter-spacing: 0.18em;
  margin-bottom: 2px;
}
.hub-streak__title { font-size: 14px; font-weight: 600; color: var(--text-primary); }
.hub-streak__claim {
  padding: 8px 14px; border-radius: var(--r-lg, 10px);
  background: rgba(var(--accent-primary-rgb), 0.18);
  border: 1px solid rgba(var(--accent-primary-rgb), 0.4);
  color: var(--accent-primary);
  font-family: 'Space Grotesk', sans-serif; font-size: 11px; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase; cursor: pointer;
  min-height: 36px;
}
.hub-streak__claim.is-claimed {
  background: rgba(73, 86, 112, 0.15);
  border-color: rgba(73, 86, 112, 0.25);
  color: var(--text-secondary);
  cursor: default;
}
.hub-streak__track { display: flex; gap: 4px; }
.hub-streak__day {
  flex: 1;
  padding: 8px 4px;
  border-radius: var(--r-md, 8px);
  background: rgba(21, 24, 33, 0.5);
  border: 1px solid rgba(73, 86, 112, 0.2);
  display: flex; flex-direction: column; align-items: center; gap: 3px;
  position: relative;
}
.hub-streak__day.is-weekly { flex: 1.4; }
.hub-streak__day.is-today {
  background: rgba(var(--accent-primary-rgb), 0.14);
  border-color: rgba(var(--accent-primary-rgb), 0.5);
}
.hub-streak__day.is-past {
  border-color: rgba(var(--accent-primary-rgb), 0.18);
}
.hub-streak__day.is-future { opacity: 0.55; }
.hub-streak__day-label {
  font-size: 8px;
  color: var(--text-secondary);
  letter-spacing: 0.06em;
}
.hub-streak__day.is-today .hub-streak__day-label { color: var(--accent-primary); }
.hub-streak__day-dot {
  width: 18px; height: 18px;
  display: flex; align-items: center; justify-content: center;
  color: var(--accent-primary);
  background: rgba(var(--accent-primary-rgb), 0.14);
  border-radius: var(--r-xs, 4px);
}
.hub-streak__day.is-past .hub-streak__day-dot {
  background: rgba(var(--accent-primary-rgb), 0.25);
}
.hub-streak__day.is-weekly .hub-streak__day-dot {
  width: 26px; height: 26px;
  background: linear-gradient(135deg, #FFD600, #FF8800);
  color: #050507;
  box-shadow: 0 0 8px rgba(255, 214, 0, 0.6);
  border-radius: var(--r-md, 6px);
}
.hub-streak__day-val {
  font-size: 9px;
  font-weight: 600;
  color: var(--text-primary);
}

.hub-resources {
  display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
}
.hub-currency {
  padding: 10px 12px; border-radius: var(--r-xl, 12px);
  background: rgba(13, 14, 20, 0.75);
  border: 1px solid rgba(73, 86, 112, 0.2);
  display: flex; align-items: center; gap: 10px;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  min-height: 56px;
}
.hub-currency__icon {
  width: 32px; height: 32px; border-radius: var(--r-md, 8px);
  background: color-mix(in srgb, var(--currency-color) 15%, transparent);
  color: var(--currency-color);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.hub-currency__info { flex: 1; min-width: 0; }
.hub-currency__label {
  color: var(--currency-color);
  letter-spacing: 0.14em;
  margin-bottom: 2px;
}
.hub-currency__value {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary);
}

.hub-codex {
  width: 100%; text-align: left;
  padding: 14px; border-radius: var(--r-2xl, 14px);
  border: 1px solid rgba(var(--accent-primary-rgb), 0.2);
  cursor: pointer;
  display: flex; align-items: center; gap: 14px;
  color: inherit;
  font-family: inherit;
  min-height: 56px;
}
.hub-codex__ring { flex-shrink: 0; }
.hub-codex__body { flex: 1; min-width: 0; }
.hub-codex__header { display: flex; align-items: center; gap: 6px; margin-bottom: 3px; }
.hub-codex__kicker { color: var(--accent-primary); letter-spacing: 0.18em; }
.hub-codex__count { color: var(--border-default); }
.hub-codex__line1 {
  font-size: 13px; color: var(--text-primary); font-weight: 500;
  margin-bottom: 2px;
}
.hub-codex__line2 { font-size: 11px; color: var(--text-secondary); }
.hub-codex__fam { font-weight: 600; }
.hub-codex__chevron { color: var(--accent-primary); flex-shrink: 0; }

.hub-codex .progress-ring { display: block; }
.hub-codex .progress-ring__meter {
  stroke-width: 3; stroke-linecap: round;
  transition: stroke-dasharray 0.6s var(--ease-spring);
}
.hub-codex .progress-ring__track { stroke-width: 3; }
.progress-ring__label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px; font-weight: 700;
  fill: var(--text-primary);
}

.hub-nav { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
.hub-navbtn {
  padding: 12px 8px; border-radius: var(--r-xl, 12px);
  background: rgba(13, 14, 20, 0.6);
  border: 1px solid rgba(73, 86, 112, 0.2);
  color: var(--text-secondary);
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  cursor: pointer;
  transition: color 0.2s var(--ease-snap), border-color 0.2s var(--ease-snap);
  font-family: inherit;
  min-height: 56px;
}
.hub-navbtn:hover, .hub-navbtn:focus-visible {
  color: var(--accent-primary);
  border-color: rgba(var(--accent-primary-rgb), 0.45);
  outline: none;
}
.hub-version {
  text-align: center;
  font-family: 'JetBrains Mono', monospace;
  font-size: 8px; letter-spacing: 0.2em;
  color: var(--border-default);
  padding-top: 6px;
}
`;
